import * as Sentry from '@sentry/react';
import { fetchFromApi } from './api-client';
import { createLogger, LogLevel } from './logger';
import { deleteCacheEntry, forgetCollection, getCacheEntry, putCacheEntry, type CacheRef } from '@/db/services/http_cache_service';
import type { PageManifest } from '@/db/types/http_cache';

/**
 * Conditional GET over a paginated Mon Ami collection, one validator per page.
 *
 * An ETag validates one representation — `?page=2` is its own resource — so a
 * 304 on one page says nothing about the others. Every refresh therefore walks
 * every page; an unchanged page costs a single 304. To make that 304 useful,
 * each page's httpCache row also carries a manifest (the ids it returned and
 * the collection's total_pages / total_count), so a caller can still assemble
 * the full id set the server holds and prune local rows that fell off it.
 *
 * CONTRACT with the server, and what covers everything else:
 * - A page's validator MUST change whenever that page's rows change. That is
 *   all HTTP promises and all we assume of the ETag. Rails' relation cache key
 *   is COUNT + MAX(updated_at) of the page's rows, which does not move when a
 *   deletion ahead of the page swaps one row for another, nor when one row is
 *   swapped for another by something that never touches it (a client
 *   reassignment). Mon Ami adds content and permission stamps to that key,
 *   and today also folds the collection's total_count in; how the server
 *   keeps the tag honest is its business, and this client does not depend on
 *   total_count being in it. Whatever the tag misses can only leave a stale
 *   row on the device, never drop one: growth past a full last page is found
 *   by the probe below, a membership change elsewhere shows up as a 200 on
 *   the pages it moved, and every complete walk reconciles against the ids
 *   the server lists. Documents key on the client's whole scope.
 * - Growth past the last known page cannot be seen through a 304 (no body), so
 *   whenever every known page answered 304 and the last one is full, one page
 *   past the end is probed without a validator.
 * - A 304 vouches for rows only if every id in its manifest is local; a page
 *   whose rows have gone missing locally is refetched in the same walk.
 * - Pages of one walk must describe one snapshot. A 304's manifest records the
 *   collection's total_count as it was; when a 200 in the same walk reports a
 *   different count, membership changed and page boundaries may have moved, so
 *   every such 304 page is refetched. Pages that answered 200 must agree on
 *   total_count and the ids collected must number total_count, or the walk is
 *   reported incomplete and nothing is committed. All of that depends on the
 *   body's `pagination.total_count`: without it the shift refetch and the
 *   add-up checks are inert (logged once per walk) and only total_pages drives
 *   the walk; a body with no pagination block at all that fills a page cannot
 *   be known complete, so nothing is pruned or committed for it.
 *
 * Nothing is persisted until the caller calls `commit()` after storing the
 * rows: a validator on record for rows we never managed to store would buy a
 * 304 that reports them present when they are not.
 */

const logger = createLogger('PagedCollection', LogLevel.DEBUG);

/** Conditions worth one Sentry message per session per collection, not one per refresh. */
const reported = new Set<string>();
function reportOnce(key: string, message: string): void {
  if (reported.has(key)) return;
  reported.add(key);
  Sentry.captureMessage(message, 'warning');
}

type RecordId = number | string;

/** Pages of one collection walked before giving up; a server-supplied count is never trusted past this. */
export const MAX_PAGES = 100;

/**
 * The server's default `per_page` (the app never asks for another). Under
 * id-ascending order a new record lands on the last page, so growth is
 * invisible to validators only when that page is exactly full; a shorter last
 * page gains the row and its validator changes. Used only when nothing better
 * is known: a response that reports `per_page`, or a multi-page collection,
 * whose first page is full by construction.
 */
export const DEFAULT_PAGE_SIZE = 25;

interface PageState {
  eTag: string | null;
  lastModified: string | null;
  manifest: PageManifest;
}

export interface CollectionPage<T> {
  page: number;
  /** Rows from a 200. Empty on a 304 — the caller already holds them. */
  records: T[];
  /** Ids this page holds, from the body or, on a 304, the stored manifest. */
  ids: RecordId[];
  totalPages: number;
  totalCount: number | null;
  pageSize?: number;
  /** A 200 body carried a `pagination` block at all. */
  paginated?: boolean;
  notModified: boolean;
  /** True for a 200, or a 304 whose manifest is known. */
  ok: boolean;
  /** The HTTP response was 2xx (false for a 304, an error, or a thrown fetch). */
  httpOk: boolean;
  status: number | null;
  /** What a 200 leaves to persist once its rows are stored. */
  pending?: PageState;
  /** A 304's stored validators, kept so a corrected manifest can be written next to them. */
  stored?: { eTag: string | null; lastModified: string | null };
  /** The page size an earlier walk stored for this page, whatever this response said. */
  rememberedPageSize?: number;
}

export interface Collection<T> {
  /** Rows from every page that answered 200. */
  records: T[];
  /** Every id the server currently holds, across all pages. Only trustworthy when `complete`. */
  ids: Set<RecordId>;
  /** The collection's page count as best known after the walk. */
  totalPages: number;
  /**
   * Every page answered 200 or a usable 304, the pages describe one snapshot
   * (total_count agrees and the ids add up), and the walk was not capped.
   * The only state in which pruning against `ids` is safe.
   */
  complete: boolean;
  /** Every page answered 304 and vouched for rows that are all local — nothing to store. */
  unchanged: boolean;
  /** Page 1's HTTP status, for callers that special-case 404. */
  status: number | null;
  firstPageOk: boolean;
  /** Page 1's HTTP response was 2xx — with `firstPageOk` false, its body was unusable. */
  httpOk: boolean;
  /**
   * Record the validators and manifests of the pages that answered 200, and
   * forget pages past the end of the collection. Call once the rows are
   * stored; until then the next refresh fetches those pages in full again.
   * Persists nothing after a walk whose pages did not describe one snapshot.
   */
  commit(): Promise<void>;
}

export interface CollectionOptions<T> {
  /** The organization whose collection this is; cache rows never cross organizations. */
  organizationId: number;
  /** Stable per-collection name; each page is cached under it. */
  cacheKey: string;
  url: (page: number) => string;
  /** Extract the rows from a 200 body; null when the body has an unexpected shape. */
  parse: (data: unknown) => T[] | null;
  /**
   * Which of these ids exist locally. A 304 page whose manifest names an id
   * that is not local is refetched: its rows were removed locally (a pruned
   * client reassigned, a failed store) and the server has no reason to resend
   * them on its own. Ids are server primary keys, unique across organizations,
   * so the lookup needs no organization scope.
   */
  localIds: (ids: RecordId[]) => Promise<Set<RecordId>>;
  timeout?: number;
}

/** Later pages in flight at once; refreshDocuments walks one collection per client on top of this. */
const PAGE_CONCURRENCY = 4;

const pageName = (cacheKey: string, page: number) => `${cacheKey}_page_${page}`;

const pageRef = <T>(options: CollectionOptions<T>, page: number): CacheRef => ({
  organizationId: options.organizationId,
  name: pageName(options.cacheKey, page),
  collection: options.cacheKey,
});

const failedPage = <T>(page: number, status: number | null, { notModified = false, httpOk = false } = {}): CollectionPage<T> => ({
  page, records: [], ids: [], totalPages: 0, totalCount: null, notModified, ok: false, httpOk, status,
});

interface PageFetchOptions {
  /** Send no validator (and drop any stored one): the stored state is known to be untrustworthy. */
  validate?: boolean;
}

export async function fetchCollectionPage<T extends { id?: RecordId | null }>(
  page: number,
  options: CollectionOptions<T>,
  { validate = true }: PageFetchOptions = {},
): Promise<CollectionPage<T>> {
  const ref = pageRef(options, page);
  const entry = await getCacheEntry(ref);

  // Drop the stored entry when it must not be trusted: an orphan validator
  // without its manifest would buy a 304 we cannot interpret, and a caller
  // asking for a validator-less fetch has already proven the entry stale (rows
  // not local, count outdated, or a page past the ceiling). Deleting before the
  // request fails safe — if this fetch fails, the next walk fetches in full
  // rather than 304 against state we know is wrong.
  const manifest = validate ? entry?.manifest ?? null : null;
  if (entry && (!validate || !manifest)) await deleteCacheEntry(ref);

  const { response, notModified, eTag, lastModified } = await fetchFromApi(options.url(page), {
    storedETag: manifest ? entry?.etag ?? undefined : undefined,
    storedLastModified: manifest ? entry?.last_modified ?? undefined : undefined,
    timeout: options.timeout,
  });

  if (notModified) {
    if (manifest) {
      return {
        page, records: [], ids: manifest.ids, totalPages: manifest.totalPages, totalCount: manifest.totalCount, pageSize: manifest.pageSize,
        notModified: true, ok: true, httpOk: false, status: 304,
        stored: { eTag: entry?.etag ?? null, lastModified: entry?.last_modified ?? null },
        rememberedPageSize: manifest.pageSize,
      };
    }
    return failedPage(page, 304, { notModified: true });
  }

  const status = response.status ?? null;
  if (!response.ok) return failedPage(page, status);

  const data: unknown = await response.json();
  const records = options.parse(data);
  if (!records) return failedPage(page, status, { httpOk: true });

  const { totalPages, totalCount, pageSize, paginated } = paginationBody(data);
  const ids = records.map(record => record.id).filter((id): id is RecordId => id != null);
  const described: PageManifest = { ids, totalPages, totalCount, ...(pageSize != null ? { pageSize } : {}) };

  return {
    page, records, ids, totalPages, totalCount, pageSize, paginated,
    notModified: false, ok: true, httpOk: true, status,
    pending: { eTag: eTag ?? null, lastModified: lastModified ?? null, manifest: described },
    rememberedPageSize: entry?.manifest?.pageSize,
  };
}

export async function fetchCollection<T extends { id?: RecordId | null }>(options: CollectionOptions<T>): Promise<Collection<T>> {
  const first = await fetchCollectionPage(1, options);
  const pages: CollectionPage<T>[] = [first];
  let capped = false;

  // Walk until the highest page count any page reported is satisfied. Page 1's
  // count may be as old as its manifest (a 304), while a later 200 knows the
  // collection has grown; stopping short would report the walk complete and let
  // callers prune the unfetched pages' rows as gone from the server.
  let ceiling = first.ok ? first.totalPages : 0;
  const walk = async (from: number) => {
    for (let next = from; next <= ceiling; ) {
      if (pages.length >= MAX_PAGES) {
        // The fetched pages are still committed, so after the first capped walk
        // these requests are 304s; only the prune stays off until the
        // collection shrinks back under the cap.
        logger.error(`[${options.cacheKey}] stopped at ${MAX_PAGES} pages with ${ceiling} reported; not pruning`);
        reportOnce(`cap:${options.cacheKey}`, `paged-collection: ${options.cacheKey} exceeds ${MAX_PAGES} pages (${ceiling} reported); pruning disabled`);
        capped = true;
        return;
      }
      const batch = Array.from({ length: Math.min(PAGE_CONCURRENCY, ceiling - next + 1, MAX_PAGES - pages.length) }, (_, i) => next + i);
      const settled = await Promise.allSettled(batch.map(page => fetchCollectionPage(page, options)));
      settled.forEach((result, i) => {
        const page = result.status === 'fulfilled' ? result.value : failedPage<T>(batch[i], null);
        pages.push(page);
        if (page.ok) ceiling = Math.max(ceiling, page.totalPages);
      });
      next += batch.length;
    }
  };
  await walk(2);

  /** Pages are pushed in order, but count by page number rather than by position. */
  const highestPage = () => Math.max(...pages.map(page => page.page));
  const vouching = () => pages.filter(page => page.ok && page.notModified);
  const nonNull = (value: number | null | undefined): value is number => value != null;
  /** The collection size as a 200 in this walk reports it; null when none did or they disagree. */
  const currentCount = () => {
    const counts = new Set(pages.filter(page => page.ok && page.httpOk).map(page => page.totalCount).filter(nonNull));
    return counts.size === 1 ? [...counts][0] : null;
  };

  // Refetch, validator-less, the 304 pages whose manifests we cannot trust.
  const refetch = async (stale: CollectionPage<T>[], why: string) => {
    for (let i = 0; i < stale.length; i += PAGE_CONCURRENCY) {
      const batch = stale.slice(i, i + PAGE_CONCURRENCY);
      logger.debug(`[${options.cacheKey}] page(s) ${batch.map(page => page.page).join(', ')} answered 304 but ${why}; refetching`);
      const settled = await Promise.allSettled(batch.map(page => fetchCollectionPage(page.page, options, { validate: false })));
      settled.forEach((result, j) => {
        const fresh = result.status === 'fulfilled' ? result.value : failedPage<T>(batch[j].page, null);
        pages[pages.indexOf(batch[j])] = fresh;
        if (fresh.ok) ceiling = Math.max(ceiling, fresh.totalPages);
      });
    }
    if (stale.length > 0 && ceiling > highestPage()) await walk(highestPage() + 1);
  };

  // A 304 says the page's rows are what they were; it does not say they are
  // still local (a client unassigned and later reassigned, a store that failed),
  // nor that the collection is the size its manifest recorded. Every 304 page,
  // including one found by a later walk, passes through here; the loop ends
  // when an iteration refetches nothing (each refetch removes a 304 page, so
  // it terminates).
  const localityChecked = new Set<number>();
  const verify = async (): Promise<boolean> => {
    const stale = new Map<number, { page: CollectionPage<T>; why: string }>();
    const unchecked = vouching().filter(page => !localityChecked.has(page.page));
    if (unchecked.length > 0) {
      unchecked.forEach(page => localityChecked.add(page.page));
      const local = await options.localIds(unchecked.flatMap(page => page.ids));
      for (const page of unchecked) {
        if (!page.ids.every(id => local.has(id))) stale.set(page.page, { page, why: 'its rows are not all local' });
      }
    }
    const count = currentCount();
    if (count != null) {
      // A deletion ahead of a page shifts rows across its boundary: its manifest
      // may name a row that is gone and omit one that moved onto it, while its
      // own validator (COUNT + MAX(updated_at)) never moved.
      for (const page of vouching()) {
        if (page.totalCount !== count) stale.set(page.page, { page, why: 'the collection changed size since' });
      }
      // The counts agree, yet the ids do not add up (a reorder across a page
      // boundary duplicates one row and drops another): the 304 pages are the
      // only ones that can be wrong without saying so. Never while a page is
      // missing or the walk is capped — the ids cannot add up then anyway, and
      // a flaky page must not cost a full re-read.
      if (stale.size === 0 && !capped && pages.every(page => page.ok)) {
        const collected = new Set(pages.flatMap(page => page.ids)).size;
        if (collected !== count) {
          for (const page of vouching()) stale.set(page.page, { page, why: 'the ids do not add up' });
        }
      }
    } else if (pages.every(page => page.ok && page.notModified)) {
      // No 200 to measure against: the manifests must at least agree with each
      // other and add up. If they do not, one of them is from a torn or
      // uncommitted walk and we cannot tell which, so all are suspect.
      const counts = new Set(vouching().map(page => page.totalCount).filter(nonNull));
      const ids = new Set(vouching().flatMap(page => page.ids)).size;
      if (counts.size > 1 || (counts.size === 1 && ids !== [...counts][0])) {
        for (const page of vouching()) stale.set(page.page, { page, why: 'the manifests disagree about the collection' });
      }
    }
    if (stale.size === 0) return false;
    const byReason = new Map<string, CollectionPage<T>[]>();
    for (const { page, why } of stale.values()) byReason.set(why, [...(byReason.get(why) ?? []), page]);
    for (const [why, batch] of byReason) await refetch(batch, why);
    return true;
  };
  // Bounded by construction: a refetch is validator-less, so its page can never
  // vouch again (a 200, or a failed page — even a stray 304 lands in failedPage
  // because there is no manifest), and new vouching pages come only from walk,
  // which MAX_PAGES caps. Rounds ≤ pages that ever vouched ≤ MAX_PAGES, so the
  // round cap is unreachable today; it is insurance against a future change to
  // refetch, not something to write a test for.
  let verifyExhausted = false;
  const settle = async () => {
    for (let rounds = 0; await verify(); rounds++) {
      if (rounds >= MAX_PAGES) {
        logger.error(`[${options.cacheKey}] verification did not settle after ${MAX_PAGES} rounds; not pruning`);
        verifyExhausted = true;
        return;
      }
    }
  };
  if (first.ok) await settle();

  // The server's page size, freshest source first: per_page reported by a 200
  // in this walk; the length of a full first page (in a multi-page collection
  // page 1 is full by construction); what earlier walks measured and stored in
  // the manifests (so a collection that shrank to one page keeps what it
  // learned); else the server default the app relies on.
  const reportedNow = pages.filter(page => page.httpOk).map(page => page.pageSize).filter(nonNull);
  const measuredNow = highestPage() > 1 ? Math.max(...pages.filter(page => page.ok).map(page => page.ids.length)) : null;
  const remembered = pages.map(page => page.rememberedPageSize).filter(nonNull);
  const pageSize = reportedNow.length > 0 ? Math.max(...reportedNow)
    : measuredNow ?? (remembered.length > 0 ? Math.max(...remembered) : DEFAULT_PAGE_SIZE);
  // What the manifests written by this walk should remember: a reported value
  // beats a measured one, which beats what earlier walks stored; the default is
  // an assumption and is never written down.
  const learnedPageSize = reportedNow.length > 0 || measuredNow != null || remembered.length > 0 ? pageSize : undefined;
  if (reportedNow.length === 0 && measuredNow != null && measuredNow !== DEFAULT_PAGE_SIZE) {
    reportOnce(`page-size:${options.cacheKey}`, `paged-collection: ${options.cacheKey} pages by ${measuredNow}, not the assumed ${DEFAULT_PAGE_SIZE}; growth detection relies on the measured value`);
  }

  // Every page 304'd: the ceiling is only as fresh as the manifests, so a
  // collection that grew a page past the last one we know is invisible. Probe
  // one page past the end, validator-less — but only when the last known page
  // is full, since growth would otherwise have landed on it and changed its
  // validator. A row on that undiscovered page may already be local (pushed by
  // pullDocument), so a probe that fails leaves the walk incomplete: pruning
  // against ids that cannot include it would delete it.
  let probeFailed = false;
  if (first.ok && !capped && !verifyExhausted && pages.every(page => page.ok && page.notModified)) {
    const last = pages.find(page => page.page === highestPage())!;
    if (last.ids.length > 0 && last.ids.length >= pageSize) {
      const probePage = Math.max(ceiling, 1) + 1;
      const probe = await fetchCollectionPage(probePage, options, { validate: false }).catch(() => failedPage<T>(probePage, null));
      if (!probe.ok) {
        probeFailed = true;
        logger.warn(`[${options.cacheKey}] could not probe page ${probePage} for growth (${probe.status}); not pruning`);
      } else if (probe.records.length > 0 || probe.totalPages > ceiling) {
        pages.push(probe);
        ceiling = Math.max(ceiling, probe.totalPages);
        await walk(probe.page + 1);
        await settle();
      } else if (probe.totalCount != null) {
        // Nothing past the end, but the probe's count is current: a size that
        // differs from the manifests' means membership changed without any
        // page validator moving.
        const outdated = vouching().filter(page => page.totalCount !== probe.totalCount);
        if (outdated.length > 0) {
          await refetch(outdated, 'the collection changed size since');
          await settle();
        }
      }
    }
  }

  // Every check below leans on total_count. A 200 without one cannot be
  // checked, so say so rather than pass silently; and a 200 with no pagination
  // block at all that fills a page may be the first of many — the walk cannot
  // be known complete.
  const uncounted = pages.filter(page => page.httpOk && page.totalCount == null);
  if (uncounted.length > 0) {
    logger.warn(`[${options.cacheKey}] page(s) ${uncounted.map(page => page.page).join(', ')} reported no total_count; the snapshot cannot be checked`);
  }
  const unpagedFull = pages.filter(page => page.httpOk && !page.paginated && page.ids.length >= pageSize);
  if (unpagedFull.length > 0) {
    logger.warn(`[${options.cacheKey}] page(s) ${unpagedFull.map(page => page.page).join(', ')} filled a page without pagination; cannot know the collection ended; not pruning`);
  }

  // One walk must describe one snapshot. Pages that answered 200 must agree on
  // the collection's size, and the ids collected must add up to it (they cannot
  // when the walk was capped, so only agreement is required then).
  const okPages = pages.filter(page => page.ok);
  const ids = new Set(okPages.flatMap(page => page.ids));
  const currentCounts = new Set(okPages.filter(page => page.httpOk).map(page => page.totalCount).filter(nonNull));
  const established = currentCounts.size === 1 ? [...currentCounts][0] : null;
  const manifestCounts = new Set(vouching().map(page => page.totalCount).filter(nonNull));
  const totalCount = established ?? (manifestCounts.size === 1 ? [...manifestCounts][0] : null);
  // The ids cannot add up when a page failed or the walk was capped; that alone
  // must not cost the pages that did answer their validators (their rows were
  // stored, and the next walk re-verifies them). Disagreeing counts still do.
  const allOk = pages.every(page => page.ok);
  const consistent = currentCounts.size <= 1 && manifestCounts.size <= 1 && (capped || !allOk || totalCount == null || ids.size === totalCount);
  if (!consistent) {
    // With every 304 page re-read by verify() when the ids did not add up,
    // this is a mid-walk mutation among 200 pages; the next walk is all-200
    // and heals itself.
    logger.warn(`[${options.cacheKey}] pages disagree about the collection (counts ${[...currentCounts, ...manifestCounts].join('/')}, ${ids.size} ids); not pruning or committing`);
  }

  // The freshest page count: any 200 in this walk is current; a manifest's is
  // as old as its validator. This is what the sweep trusts.
  const current = pages.filter(page => page.ok && page.httpOk).map(page => page.totalPages);
  const freshTotalPages = current.length > 0 ? Math.max(...current) : ceiling;
  const complete = allOk && consistent && !capped && !probeFailed && !verifyExhausted && unpagedFull.length === 0;

  return {
    records: pages.flatMap(page => page.records),
    ids,
    totalPages: freshTotalPages,
    complete,
    unchanged: complete && pages.every(page => page.notModified),
    status: first.status,
    firstPageOk: first.ok,
    httpOk: first.httpOk,
    commit: async () => {
      if (!consistent || !first.ok) return;
      const keep = Math.max(1, freshTotalPages);
      const writes = pages.flatMap(page => {
        if (page.page > keep) return [];
        if (page.pending) {
          // A manifest with no validator could never buy a 304; nothing to record.
          if (!page.pending.eTag && !page.pending.lastModified) return [];
          // A pagination-less full page's manifest would claim a one-page
          // collection nobody has measured.
          if (unpagedFull.includes(page)) return [];
          const manifest: PageManifest = { ...page.pending.manifest, ...(page.pending.manifest.pageSize == null && learnedPageSize != null ? { pageSize: learnedPageSize } : {}) };
          return [putCacheEntry(pageRef(options, page.page), { etag: page.pending.eTag, lastModified: page.pending.lastModified, manifest })];
        }
        // A 304 whose manifest carries stale collection metadata: same
        // validators, corrected page count (every page up to the ceiling
        // answered, so it is known) and, only when a 200 in this walk
        // established it, corrected total_count.
        const totalPagesNow = complete ? freshTotalPages : page.totalPages;
        const totalCountNow = established ?? page.totalCount;
        // Fresh beats stored, in what is persisted as well as in what is used.
        const pageSizeNow = learnedPageSize ?? page.pageSize;
        if (page.ok && page.stored && (page.totalPages !== totalPagesNow || page.totalCount !== totalCountNow || page.pageSize !== pageSizeNow)) {
          return [putCacheEntry(pageRef(options, page.page), { etag: page.stored.eTag, lastModified: page.stored.lastModified, manifest: { ids: page.ids, totalPages: totalPagesNow, totalCount: totalCountNow, ...(pageSizeNow != null ? { pageSize: pageSizeNow } : {}) } })];
        }
        return [];
      });
      await Promise.all(writes);
      // Pages past the end can only appear after a shrink, which always yields
      // a 200 (the emptied page); an all-304 walk has nothing to sweep.
      if (writes.length === 0 && !pages.some(page => page.httpOk)) return;
      const names = Array.from({ length: keep }, (_, i) => pageName(options.cacheKey, i + 1));
      await forgetCollection(options.organizationId, options.cacheKey, { except: names });
    },
  };
}

function paginationBody(data: unknown): { totalPages: number; totalCount: number | null; pageSize?: number; paginated: boolean } {
  let totalPages = 1;
  let totalCount: number | null = null;
  let pageSize: number | undefined;
  let paginated = false;
  if (data && typeof data === 'object' && 'pagination' in data) {
    const pagination = (data as { pagination?: { total_pages?: unknown; total_count?: unknown; per_page?: unknown } }).pagination;
    paginated = pagination != null && typeof pagination === 'object';
    if (typeof pagination?.total_pages === 'number') totalPages = pagination.total_pages;
    if (typeof pagination?.total_count === 'number') totalCount = pagination.total_count;
    if (typeof pagination?.per_page === 'number' && pagination.per_page > 0) pageSize = pagination.per_page;
  }
  return { totalPages, totalCount, pageSize, paginated };
}
